/**
 * WebsiteIntegrationCard — Zentrales Lade-Script (1 Zeile) + Pixel-Verwaltung + Cookie-Banner.
 * Pixel-Status zeigt klar, was noch fehlt. Aenderungen wirken ohne Website-Anpassung.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Check, Copy, Globe, Loader2, Save, TriangleAlert, Zap } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PIXELS = [
  { key: "meta_pixel_id", label: "Meta Pixel ID (Facebook & Instagram)", help: "Meta Events Manager → Datenquellen → Pixel (nur Ziffern)" },
  { key: "linkedin_partner_id", label: "LinkedIn Partner ID (Insight Tag)", help: "LinkedIn Campaign Manager → Analysieren → Insight Tag" },
  { key: "tiktok_pixel_id", label: "TikTok Pixel ID", help: "TikTok Ads Manager → Assets → Events" },
];

export const WebsiteIntegrationCard = ({ companyId }) => {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [gadsSetup, setGadsSetup] = useState(false);

  const autoSetupGads = async () => {
    setGadsSetup(true);
    try {
      const r = await axios.post(`${API}/google-ads/conversion-action/setup`, { company_id: companyId }, { withCredentials: true });
      toast.success(r.data.message, { duration: 9000 });
      setCfg((c) => ({ ...c, google_ads_tag_id: r.data.tag_id, google_ads_conversion_label: r.data.label }));
    } catch (e) { toast.error(e.response?.data?.detail || "Einrichtung fehlgeschlagen"); }
    finally { setGadsSetup(false); }
  };

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/webtrack/${companyId}/integration`, { withCredentials: true });
      setCfg(r.data);
    } catch { setCfg(null); }
  }, [companyId]);

  useEffect(() => { setCfg(null); if (companyId) load(); }, [companyId, load]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.post(`${API}/webtrack/${companyId}/integration`, cfg, { withCredentials: true });
      toast.success(r.data.message);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen"); }
    finally { setSaving(false); }
  };

  const scriptLine = `<script src="${process.env.REACT_APP_BACKEND_URL}/api/t/${companyId}/loader.js"></script>`;
  const missing = cfg ? PIXELS.filter((p) => !cfg[p.key]) : [];

  if (!cfg) {
    return <Card data-testid="website-integration-card"><CardContent className="py-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></CardContent></Card>;
  }

  return (
    <Card data-testid="website-integration-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Globe className="w-4 h-4" /> Website-Integration — zentrales Lade-Script
        </CardTitle>
        <p className="text-xs text-slate-500">
          Nur <b>diese eine Zeile</b> auf allen Seiten der Firmen-Website vor <code>&lt;/body&gt;</code> einbauen. Sie lädt automatisch: Besucher-Tracking, Lead-Erkennung, Klick-IDs (Google/Meta) <b>und</b> alle unten hinterlegten Werbe-Pixel inkl. Cookie-Banner. Änderungen hier wirken sofort — <b>ohne die Website je wieder anzufassen</b>.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-2">
          <code className="flex-1 text-xs bg-slate-50 border border-[var(--studio-border)] rounded-lg px-3 py-2 overflow-x-auto whitespace-nowrap" data-testid="website-integration-script">{scriptLine}</code>
          <Button size="sm" variant="outline" className="gap-1 shrink-0"
            onClick={async () => { try { await navigator.clipboard.writeText(scriptLine); toast.success("Script kopiert"); } catch { toast.error("Kopieren nicht erlaubt"); } }}
            data-testid="website-integration-copy-btn">
            <Copy className="w-3.5 h-3.5" /> Kopieren
          </Button>
        </div>

        {missing.length > 0 && (
          <div className="flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="website-integration-missing-warning">
            <TriangleAlert className="w-4 h-4 shrink-0 mt-0.5" />
            <span>
              <b>Noch nicht hinterlegt:</b> {missing.map((p) => p.label.split(" (")[0]).join(", ")}.
              Ohne diese IDs laufen die entsprechenden Kanäle ohne Retargeting & Conversion-Messung — unten ergänzen, bevor das Script eingebaut wird.
            </span>
          </div>
        )}

        <div className="grid md:grid-cols-3 gap-3">
          {PIXELS.map((p) => (
            <div key={p.key} className="space-y-1 border border-[var(--studio-border)] rounded-lg p-3">
              <div className="flex items-center justify-between gap-1">
                <label className="text-xs font-medium">{p.label.split(" (")[0]}</label>
                {cfg[p.key]
                  ? <Badge className="text-[9px] bg-emerald-100 text-emerald-700 border-emerald-200 gap-0.5"><Check className="w-2.5 h-2.5" /> hinterlegt</Badge>
                  : <Badge variant="outline" className="text-[9px] text-amber-700 bg-amber-50 border-amber-200">⚠ fehlt</Badge>}
              </div>
              <Input className="h-8 text-sm" value={cfg[p.key]} placeholder="ID eintragen"
                onChange={(e) => setCfg((c) => ({ ...c, [p.key]: e.target.value.trim() }))}
                data-testid={`integration-${p.key}-input`} />
              <p className="text-[10px] text-slate-400">{p.help}</p>
            </div>
          ))}
        </div>

        <div className="border border-[var(--studio-border)] rounded-lg p-3 space-y-3">
          <div className="flex items-center justify-between gap-2">
            <div>
              <p className="text-xs font-medium">Cookie-Banner (für die Werbe-Pixel)</p>
              <p className="text-[11px] text-slate-500">
                Erscheint als dezente Leiste am unteren Rand — <b>passt sich automatisch der Website an</b> (übernimmt deren Schrift und deine Markenfarbe) und <b>verändert das bestehende Layout nicht</b>. Pixel laden erst nach «Akzeptieren»; das Basis-Tracking läuft immer (First-Party, anonym).
              </p>
            </div>
            <Switch checked={cfg.consent_banner_enabled}
              onCheckedChange={(v) => setCfg((c) => ({ ...c, consent_banner_enabled: v }))}
              data-testid="integration-banner-switch" />
          </div>
          {cfg.consent_banner_enabled && (
            <div className="grid md:grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="text-xs text-slate-500">Banner-Text (leer = Standardtext) — gilt für Deutsch; Englisch/Französisch/Italienisch werden automatisch anhand der Browser-Sprache des Besuchers angezeigt</label>
                <Textarea rows={2} className="text-xs" value={cfg.consent_text}
                  placeholder={cfg.default_consent_text}
                  onChange={(e) => setCfg((c) => ({ ...c, consent_text: e.target.value }))}
                  data-testid="integration-consent-text-input" />
              </div>
              <div className="space-y-1">
                <label className="text-xs text-slate-500">Link zur Datenschutzerklärung</label>
                <Input className="h-8 text-sm" value={cfg.privacy_url} placeholder="https://www.deine-firma.ch/datenschutz"
                  onChange={(e) => setCfg((c) => ({ ...c, privacy_url: e.target.value.trim() }))}
                  data-testid="integration-privacy-url-input" />
              </div>
            </div>
          )}
        </div>

        <div className="border border-emerald-200 bg-emerald-50/40 rounded-xl p-3 space-y-2" data-testid="conversion-paths-section">
          <p className="text-xs font-medium">✅ Buchungs-Conversions (z.B. für app.strongfit.ch)</p>
          <p className="text-[11px] text-slate-500">
            Trage die Seite(n) ein, die nach einer <b>erfolgreichen Buchung</b> erscheinen (Bestätigungs-/Danke-Seite).
            Landet ein Besucher dort, zählt das Script automatisch eine <b>Buchung</b> — inkl. Quelle (Instagram, Flyer, Google …).
            Mehrere Pfade mit Komma trennen. Alternativ für Entwickler: <code>window.ifxBooking()</code> beim Buchungsabschluss aufrufen.
          </p>
          <Input className="h-8 text-sm" value={cfg.conversion_paths || ""}
            placeholder="/danke, /booking/success, /buchung-bestaetigt"
            onChange={(e) => setCfg((c) => ({ ...c, conversion_paths: e.target.value }))}
            data-testid="integration-conversion-paths-input" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1">
            <div>
              <label className="text-[11px] text-slate-500">Google Ads Tag-ID (AW-…) — für Conversion-Rückmeldung an Google</label>
              <Input className="h-8 text-sm" value={cfg.google_ads_tag_id || ""} placeholder="AW-123456789"
                onChange={(e) => setCfg((c) => ({ ...c, google_ads_tag_id: e.target.value }))}
                data-testid="integration-gads-tag-input" />
            </div>
            <div>
              <label className="text-[11px] text-slate-500">Google Ads Conversion-Label</label>
              <Input className="h-8 text-sm" value={cfg.google_ads_conversion_label || ""} placeholder="AbCdEfGhIj"
                onChange={(e) => setCfg((c) => ({ ...c, google_ads_conversion_label: e.target.value }))}
                data-testid="integration-gads-label-input" />
            </div>
          </div>
          <Button size="sm" variant="outline" className="h-7 text-xs gap-1 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
            onClick={autoSetupGads} disabled={gadsSetup} data-testid="integration-gads-autosetup-btn">
            {gadsSetup ? <Loader2 className="w-3 h-3 animate-spin" /> : <Zap className="w-3 h-3" />} Google-Conversion automatisch einrichten
          </Button>
          <p className="text-[10px] text-slate-400">
            Meta/TikTok/LinkedIn-Conversions werden automatisch über die Pixel zurückgemeldet. Für Google Ads: Button oben klicken — VictorFlow legt die Conversion-Aktion «Buchung (VictorFlow)» per API in deinem Google-Ads-Konto an und füllt Tag-ID + Label automatisch aus (Google Ads muss verbunden sein).
          </p>
        </div>

        <div className="flex justify-end">
          <Button size="sm" className="gap-1" onClick={save} disabled={saving} data-testid="integration-save-btn">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Speichern
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default WebsiteIntegrationCard;
